import { getAuthorization } from "redux/auth/auth.actions";
import { Ticket, TicketEntry, Comment } from "types/ticket.types";
import { Category } from "types/service.types";
import axiosDefault, { axiosXmart } from "util/axios";
import { SearchObject } from "./ticket.service.types";
import { message } from "antd";
import { Aggregator } from "components/global/issues/NewIssueModalFactory/NewTicketModal/AggregatorForm/AggregatorForm.types";
import { DetailedOrder } from "types/orders.types";

const msRoute = "/api/tickets";

export const getTicketById = async (ticketId: number): Promise<Ticket> => {
  try {
    const authorizationToken = await getAuthorization();
    const msRoute = `/api/tickets/ticket/${ticketId}`;
    const { data } = await axiosDefault.get(`${msRoute}`, {
      headers: {
        Authorization: authorizationToken
      }
    });
    return data;
  } catch (e) {
    throw new Error("Failed fetch ticket by id");
  }
};

export const createTicket = async (
  ticketRequestBody: any
): Promise<Ticket | undefined> => {
  try {
    const authorizationToken = await getAuthorization();
    const msRoute = `/api/tickets/ticket`;
    const { data } = await axiosDefault.post(`${msRoute}`, ticketRequestBody, {
      headers: {
        Authorization: authorizationToken
      }
    });
    return data;
  } catch (e) {
    const error = e.response?.data?.error?.[0];
    const { value } = error;
    throw new Error(value);
  }
};

export const fetchAssigneesByExternalId = async (externalIds: string[]) => {
  try {
    const authorizationToken = await getAuthorization();
    const msRoute = `/api/tickets/reports/allTicketsByAssignee`;
    const { data } = await axiosDefault.post(
      `${msRoute}`,
      {
        assignees: externalIds
      },
      {
        headers: {
          Authorization: authorizationToken
        }
      }
    );
    return data;
  } catch (e) {
    throw new Error("Failed to fetch assignees");
  }
};

export const fetchTicketsStatusReport = async (categoryIds: string[]) => {
  try {
    const authorizationToken = await getAuthorization();
    const msRoute = `api/tickets/reports/ticketsByStatus`;
    const { data } = await axiosDefault.post(
      `${msRoute}`,
      {
        categories: categoryIds
      },
      {
        headers: {
          Authorization: authorizationToken
        }
      }
    );
    return data;
  } catch (e) {
    throw new Error("Failed to fetch assignees");
  }
};

export const fetchTicketDailyReport = async (categoryIds: string[]) => {
  try {
    const authorizationToken = await getAuthorization();
    const msRoute = `api/tickets/reports/dailyTickets`;
    const { data } = await axiosDefault.post(
      `${msRoute}`,
      {
        categories: categoryIds
      },
      {
        headers: {
          Authorization: authorizationToken
        }
      }
    );
    return data;
  } catch (e) {
    throw new Error("Failed to fetch assignees");
  }
};

export const fetchDailyStatisticsReport = async (
  categoryIds: string[],
  assignee?: string
) => {
  try {
    const authorizationToken = await getAuthorization();
    const msRoute = `api/tickets/reports/dailyTicketsByStatus`;
    const { data } = await axiosDefault.post(
      `${msRoute}`,
      {
        categories: categoryIds,
        assignee
      },
      {
        headers: {
          Authorization: authorizationToken
        }
      }
    );
    return data;
  } catch (e) {
    throw new Error("Failed to fetch daily statistics report");
  }
};

export const updateTicket = async (
  ticketId: number,
  ticket: any
): Promise<Ticket> => {
  try {
    const authorizationToken = await getAuthorization();
    const msRoute = `/api/tickets/ticket/${ticketId}`;
    const { data } = await axiosDefault.put(`${msRoute}`, ticket, {
      headers: {
        Authorization: authorizationToken
      }
    });
    return data;
  } catch (e) {
    throw new Error("Failed to update ticket");
  }
};

export const updateActivity = async (
  ticketId: number,
  activity: any
): Promise<Ticket> => {
  try {
    const authorizationToken = await getAuthorization();
    const msRoute = `/api/tickets/updateActivity/${ticketId}`;
    const { data } = await axiosDefault.put(`${msRoute}`, activity, {
      headers: {
        Authorization: authorizationToken
      }
    });
    return data;
  } catch (e) {
    throw new Error("Failed to update ticket");
  }
};

export const fetchEntries = async (
  ticketId: string
): Promise<TicketEntry[]> => {
  try {
    const authorizationToken = await getAuthorization();
    const msRoute = `/api/tickets/entries?events=TICKET.CREATED,ASSIGNEE.ASSIGNED,ACTIVITY.ASSIGNED,TICKET.COMMENT,TICKET.RESOLVED,TICKET.CLOSED&ticket=${ticketId}`;
    const { data } = await axiosDefault.get(`${msRoute}`, {
      headers: {
        Authorization: authorizationToken
      }
    });
    return data;
  } catch (e) {
    throw new Error("Failed to fetch entries");
  }
};

export const fetchTicketCategories = async (): Promise<Category[]> => {
  try {
    const authorizationToken = await getAuthorization();
    const msRoute = "/api/tickets/category";
    const { data } = await axiosDefault.get(`${msRoute}`, {
      headers: {
        Authorization: authorizationToken
      }
    });
    return data;
  } catch (e) {
    throw new Error("Failed to fetch the categories");
  }
};

export const fetchCategories = async (): Promise<Ticket | undefined> => {
  try {
    const authorizationToken = await getAuthorization();
    const { data } = await axiosDefault.get(`${msRoute}/categoryList`, {
      headers: {
        Authorization: authorizationToken
      }
    });
    return data.data;
  } catch (e) {
    message.error("Error al obtener categorias");
  }
};

export const fetchSubcategories = async (): Promise<Ticket | undefined> => {
  try {
    const authorizationToken = await getAuthorization();
    const { data } = await axiosDefault.get(`${msRoute}/subcategory`, {
      headers: {
        Authorization: authorizationToken
      }
    });

    return data;
  } catch (e) {
    message.error("Error al obtener subcategorias");
  }
};

export const saveComment = async (ticketId: string, comment: Comment) => {
  try {
    const authorizationToken = await getAuthorization();
    const msRoute = `/api/tickets/createComment/${ticketId}`;
    const { data } = await axiosDefault.post(`${msRoute}`, comment, {
      headers: {
        Authorization: authorizationToken
      }
    });
    return data;
  } catch (e) {
    throw new Error("Failed to create comment");
  }
};

export const getTicketsByAttributes = async (
  searchObject: Partial<SearchObject>
): Promise<Ticket[] | undefined> => {
  const authorizationToken = await getAuthorization();
  try {
    const msRoute = `/api/tickets/ticketsByAttributes`;
    const { data } = await axiosDefault.post(`${msRoute}`, searchObject, {
      headers: {
        Authorization: authorizationToken
      }
    });
    return data;
  } catch (error) {
    message.error("Error al obtener los tickets de esta entidad");
  }
};

export const getAggregators = async (
  account: number
): Promise<Aggregator[] | undefined> => {
  const authorizationToken = await getAuthorization();
  try {
    const msRoute = `/aggregators/account/${account}`;
    const { data } = await axiosXmart.get(`${msRoute}`, {
      headers: {
        Authorization: authorizationToken
      }
    });
    return data;
  } catch (error) {
    message.error("Error al obtener los agregadores");
  }
};

export const getAggregatorOrder = async (
  countryCode: string,
  vendorCode: string,
  aggregator: string,
  order: string
): Promise<DetailedOrder | string | undefined> => {
  const authorizationToken = await getAuthorization();
  try {
    const msRoute = `/aggregators/order/history/${countryCode}/${vendorCode}/${aggregator}/${order}`;
    const { data } = await axiosXmart.get(`${msRoute}`, {
      headers: {
        Authorization: authorizationToken
      }
    });
    return data;
  } catch (error) {
    message.error("Error al obtener detalle del pedido");
  }
};
